import { useState, useCallback, useEffect } from 'react';
import type { Dispatch } from 'redux';
import { connect, useDispatch } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';

import { getUserRoleIsProsecutorOrSuperProsecutor } from 'AuthPackage/MainModule/selectors/authSelectors';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { addNoticeAction, removeNoticeAction } from 'CorePackage/NotificationModule/actions/NoticeActions';
import { INotification } from 'CorePackage/NotificationModule/interfaces/INotification';
import { TitleWithAddButton } from 'UIPackage/MainModule/components/TitleWithAddButton';
import { ConfirmModal } from 'UIPackage/MainModule/components/ConfirmModal';
import { NoticeTypesEnum } from 'CorePackage/NotificationModule/enums/NoticeTypesEnum';
import { MessageTypesEnum } from 'CorePackage/NotificationModule/enums/MessageTypesEnum';
import { Button } from 'UIPackage/MainModule/components/Button';
import {
  getAccountOrganizationsData,
  getAccountRequirementTemplates,
} from 'CorePackage/AccountModule/selectors/accountSelectors';
import { TemplatesItem } from 'PersonalAccountPackage/MainModule/components/TemplatesItem';
import { IPageRequirementsTemplate } from 'APIPackage/AccountModule/interfaces/IPageRequirementsTemplate';
import { IRequirementsTemplate } from 'APIPackage/AccountModule/interfaces/IRequirementsTemplate';
import { IUserOrganisation } from 'APIPackage/AccountModule/interfaces/IUserOrganisation';
import { IAccountTemplate } from 'CorePackage/AccountModule/interfaces/IAccountTemplate';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';

import styles from './UserRequirements.module.scss';
import { RequirementsForm } from './RequirementsForm';
import {
  userRequirementsChangeDefault,
  userRequirementsDelete,
  userRequirementsGetTemplateList,
  userRequirementsGetTemplateListNextPage,
  userRequirementsInitPage,
  userRequirementsResetPage,
  userRequirementsSetActiveTemplateId,
} from '../actions/userRequirementsAction';
import {
  getUserRequirementsActiveTemplateId,
  getUserRequirementsPage,
  getUserRequirementsTemplateList,
  getUserRequirementsTotalCount,
} from '../selectors/userRequirementsSelectors';
import { USER_REQUIREMENTS_PAGE_SIZE } from '../constants/useRequirementsPageSize';
import { fetchKnmSupervisionTypes } from '../../../APIPackage/CatalogsModule/services/knmSupervisionTypesApi';
import { fetchSupervisionTypesDictionary } from '../../../APIPackage/DictionariesModule/services/dictionariesApi';
import { IKnmSupervisionType } from '../../../APIPackage/CatalogsModule/interfaces/IKnmSupervisionType';

const emptyRequirementsTemplate = {
  id: undefined,
  name: '',
  byDefault: false,
  knoOrganizationId: undefined,
  supervisionType: undefined,
  requirements: undefined,
};

interface IUserRequirementsStoreProps {
  isProsecutorOrSuperProsecutor: boolean;
  accountRequirementTemplates: {
    [key: string]: IAccountTemplate<IPageRequirementsTemplate> | undefined;
  };
  knoOrganisations: IUserOrganisation[];
  templatesList: IRequirementsTemplate[];
  activeTemplateId: number | 'new' | undefined;
  templatesPage: number;
  totalCount: number;
}

const UserRequirementsWithoutStore = ({
  accountRequirementTemplates,
  activeTemplateId,
  isProsecutorOrSuperProsecutor,
  knoOrganisations,
  templatesList,
  templatesPage,
  totalCount,
}: IUserRequirementsStoreProps) => {
  const dispatch = useDispatch<Dispatch<RootActions>>();
  const addNotice: (notice: INotification) => void = useCallback(
    (notice: INotification) => dispatch(addNoticeAction(notice)),
    [dispatch],
  );
  const removeNotice: (id: string) => void = useCallback((id: string) => dispatch(removeNoticeAction(id)), [dispatch]);
  const handleLoadButtonClick = useCallback(() => dispatch(userRequirementsGetTemplateListNextPage()), [dispatch]);
  const [supervisionTypes, setSupervisionTypes] = useState<Array<IKnmSupervisionType>>([]);

  const getSupervisionTypes = useCallback(async () => {
    let types = await fetchKnmSupervisionTypes(true);

    if (knoOrganisations.length) {
      const knoIds: string[] = knoOrganisations
        .filter(item => item.ervkVersionUuid)
        .map(item => item.ervkVersionUuid || '');
      const ervkTypesDictionaryValues = await fetchSupervisionTypesDictionary(knoIds);
      const ervkTypes: IKnmSupervisionType[] = ervkTypesDictionaryValues.values.map(item => ({
        guid: item.idValue,
        id: item.value.id,
        code: item.value.code || '',
        digitCode: item.value.number,
        name: item.value.title,
        enabled: true,
      }));

      types = [...types, ...ervkTypes];
    }

    setSupervisionTypes(types);
  }, [knoOrganisations, setSupervisionTypes]);

  useEffect(() => {
    getSupervisionTypes();
  }, [knoOrganisations, getSupervisionTypes]);

  useEffect(() => {
    dispatch(userRequirementsInitPage());

    return () => {
      dispatch(userRequirementsResetPage());
    };
  }, [dispatch]);

  useEffect(() => {
    if (accountRequirementTemplates) {
      dispatch(userRequirementsGetTemplateList());
    }
  }, [dispatch, accountRequirementTemplates]);

  const isLoadButtonVisible = totalCount && +USER_REQUIREMENTS_PAGE_SIZE * (templatesPage + 1) < totalCount;

  const activeTemplate = templatesList.find(template => template.id === activeTemplateId) || emptyRequirementsTemplate;
  const [activatingTemplateId, setActivatingTemplateId] = useState<number | 'new' | undefined>();
  const [isFormReset, setIsFormReset] = useState(false);
  const isTemplateWarningModalVisible = typeof activatingTemplateId === 'number' || activatingTemplateId === 'new';
  const [deletingTemplateId, setDeletingTemplateId] = useState<number | undefined>();
  const [isTemplateDeleteModalVisible, setIsTemplateDeleteModalVisible] = useState(false);

  const handleAddButtonClick = useCallback(() => dispatch(userRequirementsSetActiveTemplateId('new')), [dispatch]);

  const makeTemplatesItemCheckboxChangeHandler = (id?: number) => () => dispatch(userRequirementsChangeDefault(id));

  const handleTemplatesItemClick = (id: number) => () => {
    if (activeTemplateId !== id) {
      dispatch(userRequirementsSetActiveTemplateId(id));
    }
  };

  const handleTemplatesItemDelete = (id: number) => () => {
    setIsTemplateDeleteModalVisible(true);
    setDeletingTemplateId(id);
  };

  const handleFormReset = useCallback(() => setIsFormReset(false), []);

  const handleFormRequirementDelete = useCallback(
    (name: string, onUndo: () => void) => {
      const id = uuidv4();

      function handleRequirementDeleteUndoButtonClick() {
        onUndo();
        removeNotice(id);
      }

      addNotice({
        renderContent: (
          <>
            Требование{name ? ` "${name}"` : ''} было удалено.
            <button
              className={styles.RequirementDeleteUndoButton}
              type="button"
              onClick={handleRequirementDeleteUndoButtonClick}
            >
              Отменить
            </button>
          </>
        ),
        noticeType: NoticeTypesEnum.WARNING,
        messageType: MessageTypesEnum.DELETE_REQUIREMENT,
        id,
      });
    },
    [addNotice, removeNotice],
  );

  const handleTemplateWarningModalApply = useCallback(() => {
    setActivatingTemplateId(undefined);
    setIsFormReset(true);
  }, []);

  const handleTemplateWarningModalClose = useCallback(() => setActivatingTemplateId(undefined), []);

  const handleTemplateDeleteModalApply = useCallback(() => {
    if (deletingTemplateId) {
      dispatch(userRequirementsDelete(deletingTemplateId));
    }
  }, [deletingTemplateId, dispatch]);

  const handleTemplateDeleteModalClose = useCallback(() => setIsTemplateDeleteModalVisible(false), []);

  const deletingTemplateName = templatesList.find(template => template.id === deletingTemplateId)?.name || '';

  return (
    <div className={styles.Wrapper}>
      <div className={styles.Templates}>
        <div className={styles.Title}>
          <TitleWithAddButton
            disabled={isProsecutorOrSuperProsecutor}
            text="Шаблоны обязательных требований"
            onClick={handleAddButtonClick}
            id="userRequirementsAddButton"
          />
        </div>
        <ul className={styles.TemplatesList}>
          {!activeTemplate.id && (
            <li className={styles.TemplatesItem}>
              <TemplatesItem
                id="new"
                name="Наименование обязательного требования"
                groupName="requirementsTemplates"
                isChecked={false}
                isActive
                onCheckboxChange={makeTemplatesItemCheckboxChangeHandler()}
              />
            </li>
          )}
          {templatesList.map(template => (
            <li className={styles.TemplatesItem} key={template.id}>
              <TemplatesItem
                id={template.id}
                name={template.name}
                groupName="requirementsTemplates"
                isChecked={template.byDefault}
                isActive={template.id === activeTemplateId}
                onCheckboxChange={makeTemplatesItemCheckboxChangeHandler(template.id)}
                onClick={handleTemplatesItemClick(template.id)}
                onDelete={!isProsecutorOrSuperProsecutor ? handleTemplatesItemDelete(template.id) : undefined}
              />
            </li>
          ))}
          {!!isLoadButtonVisible && <Button text="Показать еще" color="load" onClick={handleLoadButtonClick} />}
        </ul>
      </div>
      <div className={styles.TemplateInfo}>
        <div className="shared-group-label_margin">Общая информация</div>
        <RequirementsForm
          templateName={activeTemplate.name}
          organisations={knoOrganisations}
          activeOrganisationId={activeTemplate.knoOrganizationId}
          supervisionTypes={supervisionTypes}
          activeSupervisionTypeId={activeTemplate.supervisionType}
          requirements={activeTemplate.requirements || []}
          isReset={isFormReset}
          onReset={handleFormReset}
          onRequirementDelete={handleFormRequirementDelete}
        />
      </div>
      <ConfirmModal
        visible={isTemplateWarningModalVisible}
        title="Предупреждение"
        text="Шаблон обязательного требования был изменен, несохраненные изменения будут потеряны. Вы уверены?"
        onApply={handleTemplateWarningModalApply}
        onClose={handleTemplateWarningModalClose}
      />
      {templatesList.length > 0 && (
        <ConfirmModal
          visible={isTemplateDeleteModalVisible}
          title="Удаление шаблона обязательного требования"
          text={`Вы собираетесь удалить шаблон обязательного требования "${deletingTemplateName}". Удалить?`}
          applyButtonText="Удалить"
          cancelButtonText="Закрыть"
          onApply={handleTemplateDeleteModalApply}
          onClose={handleTemplateDeleteModalClose}
        />
      )}
    </div>
  );
};

const mapStateToProps = (state: IRootState): IUserRequirementsStoreProps => ({
  isProsecutorOrSuperProsecutor: getUserRoleIsProsecutorOrSuperProsecutor(state),
  accountRequirementTemplates: getAccountRequirementTemplates(state),
  templatesList: getUserRequirementsTemplateList(state),
  totalCount: getUserRequirementsTotalCount(state),
  activeTemplateId: getUserRequirementsActiveTemplateId(state),
  templatesPage: getUserRequirementsPage(state),
  knoOrganisations: getAccountOrganizationsData(state).kno,
});

export const UserRequirements = connect(mapStateToProps)(UserRequirementsWithoutStore);
